// =============================================================================
// MeuDriver — StudentPackages
// Landing de pacotes de aulas + meus pacotes ativos — /aluno/pacotes
// =============================================================================

import { Package, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react'
import { motion } from 'framer-motion'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useSearchParams } from 'react-router-dom'
import { useEffect } from 'react'
import { toast } from 'sonner'
import { LessonPackagesCard } from '@/components/student/LessonPackagesCard'
import { useStudentPackages } from '@/hooks/usePackages'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/utils'

const STATUS_CONFIG: Record<string, { label: string; color: string; Icon: typeof CheckCircle }> = {
  active:    { label: 'Ativo',      color: 'bg-green-100 text-green-700 border-green-200',  Icon: CheckCircle },
  pending:   { label: 'Pendente',   color: 'bg-amber-100 text-amber-700 border-amber-200',  Icon: Clock },
  exhausted: { label: 'Esgotado',   color: 'bg-gray-100 text-gray-500 border-gray-200',     Icon: CheckCircle },
  cancelled: { label: 'Cancelado',  color: 'bg-red-100 text-red-600 border-red-200',        Icon: XCircle },
}

export default function StudentPackages() {
  const { data: myPackages, isLoading } = useStudentPackages()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const payment = searchParams.get('payment')
    if (payment === 'success') toast.success('Pagamento confirmado! Seu pacote está ativo.')
    if (payment === 'failure') toast.error('Pagamento não aprovado. Tente novamente.')
    if (payment === 'pending') toast.info('Pagamento em processamento. Aguarde a confirmação.')
  }, [searchParams])

  return (
    <div className="space-y-8 max-w-3xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
          <Package className="h-6 w-6 text-brand-600" />
          Pacotes de aulas
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Compre pacotes com desconto e use as aulas com qualquer instrutor.
        </p>
      </motion.div>

      {/* Comprar novo pacote */}
      <LessonPackagesCard />

      {/* Meus pacotes */}
      <section>
        <h2 className="text-base font-bold text-gray-900 mb-4">Meus pacotes</h2>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-28 rounded-2xl" />
            <Skeleton className="h-28 rounded-2xl" />
          </div>
        ) : !myPackages || myPackages.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 rounded-2xl border border-dashed border-border text-center bg-white/60">
            <AlertCircle className="h-10 w-10 text-gray-300" />
            <div>
              <p className="font-semibold text-gray-600">Nenhum pacote ainda</p>
              <p className="text-sm text-muted-foreground mt-1">
                Compre um pacote acima e comece a economizar nas suas aulas.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {myPackages.map((pkg) => {
              const pkgDef = (pkg as any).package
              const cfg    = STATUS_CONFIG[pkg.status] ?? STATUS_CONFIG.pending
              const remaining = pkg.lessons_remaining ?? 0
              const total     = pkg.lessons_total
              const pct       = Math.round(((total - remaining) / total) * 100)

              return (
                <div
                  key={pkg.id}
                  className="rounded-2xl border border-border/60 bg-white p-5 shadow-soft"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="font-bold text-gray-900">{pkgDef?.name ?? 'Pacote'}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Válido até {format(parseISO(pkg.expires_at), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                      </p>
                    </div>
                    <Badge className={cn('text-xs border shrink-0', cfg.color)}>
                      <cfg.Icon className="h-3 w-3 mr-1" />
                      {cfg.label}
                    </Badge>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Aulas usadas</span>
                      <span className="font-semibold text-gray-900">
                        {pkg.lessons_used} / {total}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-brand-500 to-teal-500 transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{remaining} aula{remaining !== 1 ? 's' : ''} restante{remaining !== 1 ? 's' : ''}</span>
                      {pkgDef?.price_per_lesson && (
                        <span>{formatCurrency(pkgDef.price_per_lesson)}/aula</span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
